import get from 'lodash/get';
import { useMemo } from 'react';
import type { InteractionContact } from '@/common/types/interaction';
import type { MissingFieldsData } from '@/state/navigation';
import { useBuyerProfileData } from './useBuyerProfileData';

interface FieldRule {
  name: string;
  label: string;
  showWhen?: {
    fieldName: string;
    values: string[];
  }[];
}

const REQUIRED_FIELDS = [
  { name: 'entityType', label: 'Buyer Type' },
  { name: 'firstName', label: 'First Name' },
  { name: 'lastName', label: 'Last Name' },
  { name: 'email', label: 'Email' },
  {
    name: 'trust.trustName',
    label: 'Trust Name',
    showWhen: [{ fieldName: 'entityType', values: ['trust'] }],
  },
  {
    name: 'trust.titleOther',
    label: 'Client Title (Other)',
    showWhen: [
      { fieldName: 'entityType', values: ['trust'] },
      { fieldName: 'trust.title', values: ['other'] },
    ],
  },
  {
    name: 'probate.estateName',
    label: 'Estate Name',
    showWhen: [{ fieldName: 'entityType', values: ['probate'] }],
  },
  {
    name: 'probate.titleOther',
    label: 'Client Title (Other)',
    showWhen: [
      { fieldName: 'entityType', values: ['probate'] },
      { fieldName: 'probate.title', values: ['other'] },
    ],
  },
  {
    name: 'entity.entityName',
    label: 'Entity Name',
    showWhen: [{ fieldName: 'entityType', values: ['entity'] }],
  },
  {
    name: 'entity.titleOther',
    label: 'Client Title (Other)',
    showWhen: [
      { fieldName: 'entityType', values: ['entity'] },
      { fieldName: 'entity.title', values: ['other'] },
    ],
  },
  {
    name: 'powerOfAttorney.firstName',
    label: 'First Name',
    showWhen: [{ fieldName: 'entityType', values: ['powerOfAttorney'] }],
  },
  {
    name: 'powerOfAttorney.lastName',
    label: 'Last Name',
    showWhen: [{ fieldName: 'entityType', values: ['powerOfAttorney'] }],
  },
  {
    name: 'powerOfAttorney.email',
    label: 'Email',
    showWhen: [{ fieldName: 'entityType', values: ['powerOfAttorney'] }],
  },
] as const satisfies FieldRule[];

function isFieldVisible(contact: InteractionContact, rule: FieldRule): boolean {
  if (!rule.showWhen) return true;

  return rule.showWhen.every((condition) => {
    const value = get(contact, condition.fieldName);
    return value != null && condition.values.includes(value);
  });
}

function validateContact(
  contact: InteractionContact,
  index: number,
): MissingFieldsData[] {
  const missing: MissingFieldsData[] = [];

  for (const rule of REQUIRED_FIELDS) {
    if (isFieldVisible(contact, rule)) {
      const value = get(contact, rule.name);
      const isEmpty = value === undefined || value === null || value === '';

      if (isEmpty) {
        missing.push({
          section: 'Buyer Profile',
          label: rule.label,
          name: `contacts.client.${index}.${rule.name}`,
          index,
          isAddable: true,
          cardId: 'buySideContacts.buyer',
        });
      }
    }
  }

  return missing;
}

export function useBuyerProfileMissingFields(clientGroupId: string) {
  const { clients, loading } = useBuyerProfileData(clientGroupId);

  const missingFields = useMemo(() => {
    if (!clients.length) return [];

    return clients.flatMap((client, index) => validateContact(client, index));
  }, [clients]);

  return {
    missingProfileFields: missingFields,
    loading,
  };
}
